import React from 'react';

import {Group} from 'src/design/atoms/Group';
import {Space} from 'src/design/atoms/Space';
import {Title} from 'src/design/atoms/Title';
import {useLocalStorage} from '@mantine/hooks';
import type {AuditLogSimpleResponse} from 'src/api/ProactApiClient';
import type {
  IChanges} from 'src/design/organisms/ObjectDiffView';
import {
  ObjectDiffView,
} from 'src/design/organisms/ObjectDiffView';
import type { PillProps} from 'src/design/atoms/Pill';
import {Pill} from 'src/design/atoms/Pill';
import {Description} from 'src/design/atoms/Description';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {auditValuesEqual, formatAuditValue} from 'src/shared/utils/auditValue';
import {AuditChildChanges} from './AuditChildChanges';

export interface AuditLogDiffProps {
  audit?: AuditLogSimpleResponse;
  header?: React.ReactNode;
  hideDescription?: boolean;
}

export const AuditLogDiff: React.FC<AuditLogDiffProps> = ({
  audit,
  header,
  hideDescription = false,
}) => {
  const [showChangesOnly, setShowChangesOnly] = useLocalStorage({
    key: 'object-diff-view-expanded',
    defaultValue: true,
  });

  const valid = !!audit?.changes;
  // Guard against corrupt / malformed `changes` blobs from legacy rows so the
  // viewer degrades gracefully instead of crashing the whole audit page.
  let parsed: Record<string, unknown> = {};
  let parseFailed = false;
  if (valid) {
    try {
      parsed = JSON.parse(audit.changes) ?? {};
    } catch {
      parseFailed = true;
    }
  }

  // Combine Action and parsed changes
  const allChanges = {
    ['Action']: {
      Original: audit?.actionType,
      Current: audit?.actionType,
    },
    ...parsed,
  } as IChanges;

  const changeType =
    audit?.actionType === 'Added'
      ? 'added'
      : audit?.actionType === 'Modified'
      ? 'updated'
      : 'deleted';

  // Filter changes based on showChangesOnly. Added/Deleted records are conceptually
  // all-changes — every field represents the creation or removal — so never hide rows
  // there even when raw Original === Current in the blob. Modified uses null-normalized
  // equality so null ↔ '' ↔ undefined don't leak through as spurious diffs.
  const changes =
    showChangesOnly && changeType === 'updated'
      ? Object.fromEntries(
          Object.entries(allChanges).filter(
            ([, values]) => !auditValuesEqual(values.Original, values.Current)
          )
        )
      : allChanges;

  const getPillConfig = (audit?: AuditLogSimpleResponse) => {
    const pills: PillProps[] = [];

    if (!audit) {
      return pills;
    }

    pills.push({text: audit?.actionType, color: 'gray'} as PillProps);
    pills.push({text: audit?.entityName, color: 'blue'} as PillProps);
    pills.push({
      text: `Entity ID ${audit?.entityId}`,
      color: 'blue',
    } as PillProps);

    return pills;
  };
  const pills = getPillConfig(audit);

  return (
    <ContentWrapper>
      {header ?? (
        <>
          <Title>Audit Log #{audit?.id}</Title> <Space pb="xs" />
          <Group gap={1}>
            {pills.map(pill => (
              <Pill {...pill} />
            ))}
          </Group>
        </>
      )}

      {!hideDescription && (
        <>
          <Description text="Audit data is system generated and managed. Field names and data values may appear different than elsewhere. Audit data is automatically deleted based on your environment's data storage agreement. Some data is automatically created, updated, or deleted by the system but may be attributed to a user as part of a user-initiated action. All dates shown are in UTC timezone." />
          <Space pb="lg" />
        </>
      )}
      <Group justify="end">
        <Checkbox
          label="Show Changes Only"
          checked={showChangesOnly}
          onChange={(_, checked) => setShowChangesOnly(checked)}
          size="xs"
          c="gray"
          pb="sm"
        />
      </Group>
      {parseFailed && (
        <Description text="Audit change data could not be parsed. The underlying record may be corrupt or stored in an older format." />
      )}
      {valid && !parseFailed && (
        <ObjectDiffView
          changes={changes}
          changeType={changeType}
          valuesEqual={auditValuesEqual}
          formatValue={formatAuditValue}
        />
      )}

      {!!audit?.childChanges?.length && (
        <>
          <Space my="lg" />
          <AuditChildChanges
            childChanges={audit.childChanges}
            showChangesOnly={showChangesOnly}
          />
        </>
      )}
    </ContentWrapper>
  );
};
